import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { ComboSelect } from '../../../components/ComboSelect';
import { LoaderSymbol } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { CellContextMenu, canBulkCopy } from '../../../components/gridEdit';
import { markUserEdit } from '../../../hooks/useUnsavedGuard';
import './preLoginSettings.css';

/**
 * Pre-Login Settings → "New Job" — the port of the legacy XAF dialog
 * `RoutesRequestBatch_DetailView` opened by `COCSettingsController.NewJobRoute`.
 *
 * <p>The ticked Pre-Login Settings are REGROUPED BY ROUTE server-side (a COC's
 * `RouteID` is a ';'-joined list, so one COC can appear on several routes). The
 * header shows the batch totals and the grid shows one row per route, in which
 * the user picks a Driver and an Expect Service Date. Column order, captions and
 * widths are the legacy `RoutesRequestBatch_Lines_ListView` ones:
 * COCID · COCName · Driver · Route · Expect Service Date.</p>
 *
 * <p>On OK one job is created per COC and one route request per row — see
 * `AI_PreLoginSettings_NewJob_SP`. The confirm wording is legacy's own
 * `dc.AcceptAction.ConfirmationMessage`.</p>
 *
 * <p>The rows are TICKABLE (legacy `ShowSelectionColumn="True"`) and a right-click
 * on a Driver or Expect Service Date cell offers legacy's row-menu "Copy To All
 * Cell" — which, despite the name, copies that cell to the TICKED rows only
 * (`RoutesRequestLinesController` loops `IsRowSelectedOnPage`), the same misnomer
 * Sample Registration's grid carries.</p>
 *
 * <p>The driver list is PER ROUTE, not global: legacy's
 * `RoutesRequestLine.DriverChoices` offers only the Collectors of the clients on
 * that row's COCs. Sharing one list across rows would offer a driver who does
 * not work for that route's client.</p>
 */

interface Totals {
  stops: number; stations: number; samples: number; container: number;
  client: string | null;
}
interface RouteRow {
  routeOid: string; routeName: string | null;
  /** This route's COC Oids, ';'-joined — posted straight back on OK so the
   *  (route, COC) pairs created are the ones the server grouped, never a
   *  grouping re-derived in the browser. */
  cocOids: string | null;
  cocIds: string | null; cocNames: string | null;
  stops: number; samples: number; container: number;
}
interface DriverRow { routeOid: string; driverOid: string; driverName: string }
interface Preview {
  totals: Totals;
  routes: RouteRow[];
  drivers: DriverRow[];
  cocsWithoutRoute: string[];
}
export interface NewJobCreated {
  cocOid: string; cocId: string | null; jobOid: string; jobID: string; expectDate: string;
}

/** What the user filled in, keyed by route Oid. */
type Entry = { driverOid: string; date: string };

export default function PreLoginNewJobDialog({ cocOids, onClose, onCreated }: {
  /** The ticked COCs, in list order. */
  cocOids: string[];
  onClose: () => void;
  onCreated: (jobs: NewJobCreated[]) => void;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [saving, setSaving]   = useState(false);
  /** Ticked rows — legacy's selection column, which is what Copy To All Cell copies to. */
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  /** The four header totals. Editable like legacy's (RoutesRequestBatch is a
   *  non-persistent object on an Edit-mode DetailView, so XAF renders them as
   *  spin edits), and informational in the same way: Dc_Accepting_NewJobRoute
   *  RECOMPUTES Stops / Stations / Samples / Container per route from that
   *  route's COCs when it writes each RoutesRequest, so what is typed here is
   *  never what is stored. */
  const [totals, setTotals] = useState<{ stops: number; stations: number; samples: number; container: number }>(
    { stops: 0, stations: 0, samples: 0, container: 0 });
  /** The right-clicked cell: which column, its value, and where to open the menu. */
  const [ctx, setCtx] = useState<
    { field: 'driver' | 'date'; label: string; value: string; text: string; x: number; y: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post('/api/v1/pre-login-settings/new-job/preview', { cocOids });
        if (cancelled) return;
        const p: Preview = res.data?.data;
        setPreview(p);
        // Every row starts with no driver and no date — legacy's grid opens the
        // same way, and ExpectServiceDate is the one required field.
        setEntries(Object.fromEntries(p.routes.map(r => [r.routeOid, { driverOid: '', date: '' }])));
        setSel(new Set());
        setTotals({
          stops: p.totals.stops, stations: p.totals.stations,
          samples: p.totals.samples, container: p.totals.container,
        });
      } catch {
        if (!cancelled) { notify.error('Could not read the routes for this selection.'); onClose(); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // cocOids is a fresh array each render in the parent, so depend on its
    // CONTENT — otherwise the preview re-fetches on every parent render and
    // wipes what the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cocOids.join(',')]);

  /** Driver options for ONE route (legacy RoutesRequestLine.DriverChoices). */
  const driversFor = useCallback((routeOid: string) =>
    (preview?.drivers ?? [])
      .filter(d => d.routeOid === routeOid)
      .map(d => ({ value: d.driverOid, label: d.driverName })),
    [preview]);

  const patch = useCallback((routeOid: string, p: Partial<Entry>) => {
    setEntries(e => ({
      ...e,
      [routeOid]: { driverOid: '', date: '', ...e[routeOid], ...p },
    }));
  }, []);

  /** Legacy "Copy To All Cell": the focused cell → the TICKED rows only. */
  const copyToSelected = useCallback((field: 'driver' | 'date', value: string) => {
    if (sel.size === 0) { notify.warning('Tick at least one row first.'); return; }
    setEntries(e => {
      const next = { ...e };
      for (const id of sel) {
        const key = String(id);
        const row = next[key] ?? { driverOid: '', date: '' };
        next[key] = field === 'driver' ? { ...row, driverOid: value } : { ...row, date: value };
      }
      return next;
    });
    markUserEdit();
  }, [sel, notify]);

  /** Opens the shared cell menu, unless the cell is empty (the standard's guard). */
  const openCtx = useCallback((
    e: React.MouseEvent, field: 'driver' | 'date', label: string, value: string, text: string) => {
    e.preventDefault();
    if (!canBulkCopy(text)) { notify.warning('Cell is empty.'); return; }
    setCtx({ field, label, value, text, x: e.clientX, y: e.clientY });
  }, [notify]);

  const columns: AppColumn<RouteRow>[] = useMemo(() => [
    { field: 'cocIds',   header: 'COCID',   width: 110 },
    { field: 'cocNames', header: 'COCName', width: 110 },
    {
      field: 'driver', header: 'Driver', width: 150,
      onCellContextMenu: (e, r) => openCtx(
        e, 'driver', 'Driver',
        entries[r.routeOid]?.driverOid ?? '',
        driversFor(r.routeOid).find(d => d.value === entries[r.routeOid]?.driverOid)?.label ?? ''),
      render: (r) => (
        <ComboSelect
          value={entries[r.routeOid]?.driverOid ?? ''}
          options={driversFor(r.routeOid)}
          placeholder={driversFor(r.routeOid).length ? 'Select…' : 'No collectors for this client'}
          onChange={v => {
            patch(r.routeOid, { driverOid: v });
            // A custom widget the unsaved tracker cannot see (standing rule 15).
            markUserEdit();
          }}
        />
      ),
    },
    { field: 'routeName', header: 'Route', width: 100 },
    {
      // The ONE required field — legacy [RuleRequiredField] on
      // RoutesRequest.ExpectServiceDate, checked again server-side.
      field: 'date', header: <>Expect Service Date <span className="req-star">*</span></>,
      headerText: 'Expect Service Date', width: 130,
      onCellContextMenu: (e, r) => openCtx(
        e, 'date', 'Expect Service Date',
        entries[r.routeOid]?.date ?? '', entries[r.routeOid]?.date ?? ''),
      render: (r) => (
        <input
          type="date"
          className={entries[r.routeOid]?.date ? undefined : 'pl-req-empty'}
          value={entries[r.routeOid]?.date ?? ''}
          onChange={e => patch(r.routeOid, { date: e.target.value })}
        />
      ),
    },
  ], [entries, driversFor, patch, openCtx]);

  const submit = async () => {
    const routes = preview?.routes ?? [];
    if (routes.length === 0) return;

    // Mirrors the server's own check (legacy AcceptAction_Executing :5977).
    if (routes.some(r => !entries[r.routeOid]?.date)) {
      notify.error('Enter the expect service date for every route.');
      return;
    }

    // Legacy's second check (:5991) — the date drives a dispatch run, so a past
    // one would schedule work for a day that has gone.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (routes.some(r => (entries[r.routeOid]?.date ?? '') < today)) {
      notify.error('The expect service date cannot be in the past.');
      return;
    }

    const ok = await confirm({
      title: 'New Job',
      // Legacy dc.AcceptAction.ConfirmationMessage, verbatim.
      message: 'Are you sure that you want to create a new Job ID for the selected COC(s) under these routes?',
      confirmLabel: 'Create',
    });
    if (!ok) return;

    setSaving(true);
    try {
      const body = {
        routes: routes.map(r => ({
          routeOid: r.routeOid,
          // Exactly the COCs the server grouped onto this route — sending them
          // per row is what lets it build one RoutesRequest per route while
          // still creating only ONE job per COC.
          cocOids: (r.cocOids ?? '').split(';').map(s => s.trim()).filter(Boolean),
          driverOid: entries[r.routeOid]?.driverOid || null,
          expectServiceDate: entries[r.routeOid]!.date,
        })),
      };
      const res = await api.post('/api/v1/pre-login-settings/new-job', body);
      const jobs: NewJobCreated[] = res.data?.data ?? [];
      onCreated(jobs);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg || 'The jobs could not be created.');
    } finally {
      setSaving(false);
    }
  };

  const blocked = preview?.cocsWithoutRoute ?? [];

  return (
    <AppDialog
      open onClose={onClose} title="Routes" icon="ti-route" width={900}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => void submit()}
                disabled={saving || loading || blocked.length > 0 || (preview?.routes.length ?? 0) === 0}>
          <i className="ti ti-check" /> OK
        </button>
      </>}
    >
      {loading ? (
        <div style={{ padding: 28, textAlign: 'center' }}><LoaderSymbol /></div>
      ) : blocked.length > 0 ? (
        // Legacy refuses the WHOLE batch when ANY selected COC has no route
        // ("The selected COCID should contain a valid route ID."). Naming them
        // is the only addition — the message alone does not say which.
        <div className="bl-err" style={{ margin: 4 }}>
          <i className="ti ti-alert-circle" />{' '}
          The selected COCID should contain a valid route ID.
          <div style={{ marginTop: 6, color: 'var(--ai-muted)' }}>
            No route on: {blocked.join(', ')}
          </div>
        </div>
      ) : (
        <>
          {/* The legacy header is TWO stacked columns, not one row of four:
              RoutesRequestBatch_col1 = Stops over Stations, _col2 = Samples over
              Container (Model.xafml RoutesRequestBatch_DetailView). */}
          <div className="pl-nj-totals">
            {([['stops', 'Stops'], ['stations', 'Stations'],
               ['samples', 'Samples'], ['container', 'Container']] as const).map(([k, label]) => (
              <div key={k}>
                <label htmlFor={`pl-nj-${k}`}>{label}</label>
                <input id={`pl-nj-${k}`} type="number" min={0} value={totals[k]}
                       onChange={e => setTotals(t => ({ ...t, [k]: Math.max(0, Number(e.target.value) || 0) }))} />
              </div>
            ))}
          </div>

          <AppDataGrid<RouteRow>
            rows={preview?.routes ?? []}
            columns={columns}
            getRowId={r => r.routeOid}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            defaultPageSize={10}
            emptyText="None of the selected records carries a route."
            hideHeaderTools
          />

          {ctx && (
            // ONE action, as legacy has: it copies to the ticked rows only.
            <CellContextMenu
              value={ctx.text}
              columnLabel={ctx.label}
              selectedCount={sel.size}
              position={{ x: ctx.x, y: ctx.y }}
              onCopyAll={() => copyToSelected(ctx.field, ctx.value)}
              onClose={() => setCtx(null)}
            />
          )}
        </>
      )}
    </AppDialog>
  );
}
